import React, {useCallback, useEffect, useState} from 'react';
import {Alert, Box, Typography} from '@mui/material';
import PropTypes from 'prop-types';
import RichEditor from './Shared/RichEditor';
import RichEditorResultText from './Shared/RichEditorResultText';

const AdditionalNotes = ({
                             componentId,
                             descriptionHtml,
                             onDescriptionHtmlChange,
                             commitDescriptionHtml,
                             isEditMode,
                             isFormArchived,
                             placeholderValues,
                             readOnly,
                             accessLevel,
                             audience,
                             recipientType,
                             linkedToStudents,
                             linkedToEmployees,
                             submitMode,
                             isEmailText = false,
                             isBuilderView = false,
                         }) => {
    const [draftDescriptionHtml, setDraftDescriptionHtml] = useState(descriptionHtml || '');
    const [isEditorFocused, setIsEditorFocused] = useState(false);

    useEffect(() => {
        if (isEditorFocused) return;
        setDraftDescriptionHtml(descriptionHtml || '');
    }, [descriptionHtml, isEditorFocused]);

    const handleDraftChange = useCallback(
        (nextDescriptionHtml) => {
            setDraftDescriptionHtml(nextDescriptionHtml);
            onDescriptionHtmlChange(nextDescriptionHtml);
        },
        [onDescriptionHtmlChange]
    );

    const handleFocus = useCallback(() => {
        setIsEditorFocused(true);
    }, []);

    const handleBlur = useCallback(() => {
        setIsEditorFocused(false);
        commitDescriptionHtml();
    }, [commitDescriptionHtml]);

    return (
        <Box sx={{mt: 2}}>
            {isEditMode
                ? (
                    <Box sx={{
                        width: '100%',
                        cursor: 'pointer'
                    }}>
                        <RichEditor
                            currentDescription={draftDescriptionHtml}
                            setCurrentDescription={handleDraftChange}
                            isFormLive={isFormArchived}
                            accessLevel={accessLevel}
                            audience={audience}
                            recipientType={recipientType}
                            linkedToStudents={linkedToStudents}
                            linkedToEmployees={linkedToEmployees}
                            placeholderValues={placeholderValues}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                            // AD-2689 (Maria): the Notes at the top of a form is the text of the email sent with it.
                            emptyText={isEmailText
                                ? 'This text will be the body of the email sent to the recipients of this form. You can personalise it with the Placeholders list.'
                                : 'Type your notes or instructions here. You can personalise them with the Placeholders list.'}
                        />

                        {draftDescriptionHtml && draftDescriptionHtml.length > 200000 && (
                            <Alert severity="warning" sx={{mt: 2}}>
                                <Typography variant="body2">
                                    <strong>Warning:</strong> This description is{' '}
                                    {draftDescriptionHtml.length.toLocaleString()} characters and exceeds the
                                    200,000-character limit for email notifications. Because of this, the description
                                    will not be included in emails, and the default description will be used instead. To
                                    include it in email notifications, shorten the content or use smaller images.
                                </Typography>
                            </Alert>
                        )}
                    </Box>
                )
                : isBuilderView && !descriptionHtml
                    ? (
                        // AD-2689 (Maria): an empty Notes keeps a box in the builder, saying what it is for.
                        <Box sx={{border: '1px solid #e3e8ef', borderRadius: 1, p: 1, minHeight: 64, color: 'text.disabled'}}>
                            {isEmailText
                                ? 'This text will be the body of the email sent to the recipients of this form. You can personalise it with the Placeholders list.'
                                : 'Type your notes or instructions here. You can personalise them with the Placeholders list.'}
                        </Box>
                    )
                    : (
                    <RichEditorResultText
                        id={componentId}
                        description={descriptionHtml}
                        placeholderValues={placeholderValues}
                        readonly={readOnly}
                        enableValidation={false}
                        audience={audience}
                        recipientType={recipientType}
                        linkedToStudents={linkedToStudents}
                        linkedToEmployees={linkedToEmployees}
                        submitMode={submitMode}
                    />
                )}
        </Box>
    );
};

AdditionalNotes.propTypes = {
    componentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    descriptionHtml: PropTypes.string,
    onDescriptionHtmlChange: PropTypes.func.isRequired,
    commitDescriptionHtml: PropTypes.func.isRequired,
    isEditMode: PropTypes.bool,
    isFormArchived: PropTypes.bool,
    accessLevel: PropTypes.number,
    audience: PropTypes.number,
    recipientType: PropTypes.string,
    linkedToStudents: PropTypes.bool,
    linkedToEmployees: PropTypes.bool,
    placeholderValues: PropTypes.object,
    readOnly: PropTypes.bool,
    submitMode: PropTypes.bool,
    isEmailText: PropTypes.bool,
    isBuilderView: PropTypes.bool,
};

export default AdditionalNotes;
